import { useState, useEffect } from "react";
import { getSession } from "@/services/sessionStorage";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  UtensilsCrossed,
  Truck,
  CreditCard,
  Banknote,
  Calendar as CalendarIcon,
  MapPin,
  Tag,
  ArrowRight,
  Clock,
  StickyNote,
} from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { useFormatPrice } from "@/utils/currency";
import { useBrand } from "@/contexts/BrandContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useBranchModal } from "@/contexts/BranchModalContext";
import { Button } from "@/components/ui/button";
import { useAuthGate } from "@/hooks/useAuthGate";
import { placeOrder } from "@/services/orderApi";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getClosedBranchMessage } from "@/utils/branchStatus";

export default function CartPage() {
  const { t, language } = useLanguage();
  const formatPrice = useFormatPrice();
  const { brand } = useBrand();
  const { session, setOrderType, setBranchStatus } = useSessionContext();
  const { openBranchModal } = useBranchModal();
  const {
    cartData,
    removeItem,
    updateQuantity,
    clearCart,
    clearCartIdEverywhere,
    resetCart,
    refreshCart,
    itemsCount,
    isMutating,
  } = useCart();
  const items = cartData.items;

  // Order type from session (persisted)
  const orderType = session.order.order_type;
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState<{
    type: string;
    value: number;
    amount: number;
    isFreeDelivery?: boolean;
  } | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<"card" | "cash">(
    "card",
  );
  // payment_method_id comes from the brand configuration
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    undefined,
  );
  const [scheduledTimeSlot, setScheduledTimeSlot] = useState("");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const isScheduled = !!scheduledDate;
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [showOrderTypeSelector, setShowOrderTypeSelector] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  useEffect(() => {
    const loadCart = async () => {
      try {
        await refreshCart();
      } catch (error) {
        console.error("Failed to refresh cart:", error);
      }
    };

    loadCart();
  }, []);
  // All values come from backend - no frontend calculations
  const subtotal = cartData.subtotal;
  const backendDiscount = cartData.discount;

  // backend free delivery
  const backendIsDeliveryFree = cartData.is_delivery_free === true;

  // promo free delivery (only valid for delivery orders)
  const promoIsFreeDelivery =
    orderType === "delivery" &&
    promoApplied &&
    promoDiscount?.type === "free_delivery";

  const promoDiscountAmount =
    promoApplied && promoDiscount ? promoDiscount.amount : 0;

  const deliveryFee = cartData.delivery_fee || 0;

  const isDeliveryFree = backendIsDeliveryFree || promoIsFreeDelivery;

  const shouldShowDeliveryFee = orderType === "delivery";

  // if promo is free delivery, promo amount should equal delivery fee
  const effectivePromoDiscountAmount = promoIsFreeDelivery
    ? deliveryFee
    : promoApplied && promoDiscount
      ? promoDiscount.amount
      : 0;

  const discount = backendDiscount + effectivePromoDiscountAmount;

  const tax = cartData.tax || 0;
  const total = cartData.total - effectivePromoDiscountAmount;

  // Minimum order check
  const orderMinimum = brand.orderMinimumAllowed || 0;
  const isBelowMinimum = orderMinimum > 0 && total < orderMinimum;
  const amountNeeded = orderMinimum - total;

  const isPromoEnabled = cartData.discount_enabled;

  // Whether branch is needed but not yet selected (pickup-only + all-branches mode)
  const needsBranchSelection =
    brand.productsAvailableInAllBranches && !session.order.branchId;

  const effectivePayment =
    brand.paymentMethod === "both"
      ? selectedPayment
      : brand.paymentMethod === "cash"
        ? "cash"
        : "card";

  const closedBranchMessage = getClosedBranchMessage(
    {
      is_open: session.order.isOpen,
      is_busy: session.order.isBusy,
      reason: session.order.branchStatusReason,
    },
    language,
  );

  const handlePlaceOrder = async () => {
    if (closedBranchMessage) {
      return;
    }

    if (!session.order.cartId) {
      toast.error("Cart is not ready. Please add items first.");
      console.error("[place-order] Missing cartId:", session.order.cartId);
      return;
    }
    if (!session.order.order_type) {
      toast.error("Please select an order type first.");
      console.error(
        "[place-order] Missing order_type:",
        session.order.order_type,
      );
      return;
    }

    const scheduledAt =
      scheduledDate && scheduledTimeSlot
        ? `${format(scheduledDate, "yyyy-MM-dd")}T${scheduledTimeSlot}:00`
        : null;

    const freshSession = getSession();
    const customerData = freshSession?.customer?.data || {};
    const firstName = (customerData.first_name as string) || "";
    const lastName = (customerData.last_name as string) || "";
    const customerName =
      [firstName, lastName].filter(Boolean).join(" ") || null;
    const customerPhone =
      (customerData.phone as string) || (customerData.mobile as string) || "";
    const customerEmail = (customerData.email as string) || null;

    if (!customerPhone) {
      toast.error(
        language === "ar"
          ? "رقم الهاتف مطلوب لإتمام الطلب"
          : "Phone number is required to place an order.",
      );
      return;
    }

    const deliveryLocation =
      session.order.order_type === "delivery"
        ? {
            latitude: session.customer.selectedAddress.latitude,
            longitude: session.customer.selectedAddress.longitude,
            street_name: session.customer.selectedAddress.streetName,
            building_number: session.customer.selectedAddress.buildingNumber,
            floor_number: session.customer.selectedAddress.floorNumber,
            apartment_number: session.customer.selectedAddress.apartmentNumber,
            geocode_address: session.customer.selectedAddress.geocodeAddress,
            details:
              session.customer.selectedAddress.details ||
              deliveryAddress ||
              null,
          }
        : null;

    const syncReturnedBranchStatus = (
      status?: {
        is_open: boolean;
        is_busy: boolean;
        reason?: string | null;
      } | null,
    ) => {
      if (!status) return null;
      setBranchStatus(status.is_open, status.is_busy, status.reason ?? null);
      return getClosedBranchMessage(status, language);
    };

    const requestPayload = {
      cart_id: session.order.cartId,
      restaurant_id: session.order.restaurantId,
      branch_id: session.order.branchId,
      order_type: session.order.order_type || "pickup",
      payment_method: effectivePayment,
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: customerEmail,
      delivery_location: deliveryLocation,
      notes: orderNotes || null,
      scheduled_at: scheduledAt,
      promo_code: promoApplied && promoDiscount ? promoCode.trim() : null,
      discount_amount:
        promoApplied && promoDiscount ? promoDiscount.amount : null,
      country_code: (session.customer?.data?.country_code as string) || "+966",
    };

    setIsPlacingOrder(true);
    try {
      const result = await placeOrder(requestPayload);
      const returnedClosedBranchMessage = syncReturnedBranchStatus(
        result.data?.branch_status ?? result.branch_status ?? null,
      );

      if (result.success && result.data) {
        // Clear cartId from ALL locations immediately (ref, state, session storage)
        // This ensures no stale cartId can be restored after order placement
        clearCartIdEverywhere();

        if (effectivePayment === "cash") {
          toast.success(t("cart.orderSuccess"));
          await clearCart();
          await resetCart(); // Generate new cart via cart-resolve
          navigate(`/track/${result.data.order_number}`, {
            state: {
              orderId: result.data.order_id,
              orderNumber: result.data.order_number,
            },
          });
          return;
        }

        await clearCart();
        if (result.data.order_payment_url) {
          window.location.href = result.data.order_payment_url;
        } else {
          navigate("/payment", {
            state: {
              orderId: result.data.order_id,
              orderNumber: result.data.order_number,
            },
          });
        }
        return;
      }

      toast.error(
        result.error?.message ||
          returnedClosedBranchMessage?.description ||
          t("cart.orderError"),
      );
    } catch {
      toast.error(t("cart.orderError"));
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!isPromoEnabled) {
      return;
    }

    const trimmed = promoCode.trim();
    if (!trimmed) {
      setPromoError(t("cart.promoInvalid"));
      return;
    }

    setPromoLoading(true);
    setPromoError("");
    setPromoApplied(false);
    setPromoDiscount(null);

    try {
      //const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseUrl = window.location.origin;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      if (!supabaseUrl || !anonKey) {
        setPromoError(t("cart.promoInvalid"));
        return;
      }

      const res = await fetch(`${supabaseUrl}/api/WebOrder/functions/v1/validate-promo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          code: trimmed,
          restaurant_id: session.order.restaurantId,
          phone_number: session.customer.data?.mobile?.toString() ?? null,
          cart_subtotal: subtotal,
        }),
      });

      const data = await res.json();

      if (data.valid) {
        const isFreeDeliveryPromo = data.discount_type === "free_delivery";

        if (isFreeDeliveryPromo && orderType !== "delivery") {
          setPromoApplied(false);
          setPromoDiscount(null);
          setPromoError(
            language === "ar"
              ? "كود الخصم صالح فقط لطلبات التوصيل"
              : "This promo code is valid for delivery orders only.",
          );
          return;
        }

        setPromoApplied(true);
        setPromoDiscount({
          type: data.discount_type,
          value: data.discount_value || 0,
          amount: isFreeDeliveryPromo ? deliveryFee : data.discount_amount || 0,
          isFreeDelivery: isFreeDeliveryPromo,
        });

        toast.success(t("cart.promoApplied"));
      } else {
        const errorMessages: Record<string, string> = {
          INVALID_CODE: t("cart.promoInvalid"),
          EXPIRED: t("cart.promoExpired"),
          NOT_STARTED: t("cart.promoNotStarted"),
          MAX_USES_REACHED: t("cart.promoMaxUses"),
          MIN_ORDER_NOT_MET: t("cart.promoMinOrder"),
        };
        setPromoError(
          errorMessages[data.error] || data.message || t("cart.promoInvalid"),
        );
      }
    } catch {
      setPromoError(t("cart.promoInvalid"));
    } finally {
      setPromoLoading(false);
    }
  };

  const orderTypeLabels: Record<
    string,
    { icon: React.ReactNode; label: string }
  > = {
    dine_in: {
      icon: <UtensilsCrossed className="h-4 w-4" />,
      label: t("order.dinein"),
    },
    pickup: {
      icon: <ShoppingBag className="h-4 w-4" />,
      label: t("order.takeaway"),
    },
    delivery: {
      icon: <Truck className="h-4 w-4" />,
      label: t("order.delivery"),
    },
  };

  if (items.length === 0 && !isMutating) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-20">
          <span className="text-6xl mb-4">🛒</span>
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
            {t("cart.empty")}
          </h1>
          <p className="text-muted-foreground mb-6">{t("cart.empty.desc")}</p>
          <Button size="lg" className="gap-2" onClick={() => navigate("/menu")}>
            {t("cart.browseMenu")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-6">
          {t("cart.title")} ({itemsCount} {t("cart.items")})
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Cart Items + Options */}
          <div className="lg:col-span-2 space-y-4">
            {/* Cart Items */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {items.map((item, idx) => {
                const name = language === "ar" ? item.name_ar : item.name;
                return (
                  <div
                    key={item.id}
                    className={`flex gap-4 p-4 ${idx > 0 ? "border-t border-border" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-heading font-bold text-foreground text-sm">
                            {name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.customizations.feature_summary.map((group) =>
                              group.values.map((value) => (
                                <Badge
                                  key={`${group.groupKey}-${value}`}
                                  variant={
                                    group.groupKey === "sizes"
                                      ? "secondary"
                                      : "outline"
                                  }
                                  className="text-[10px]"
                                >
                                  {group.groupLabel}: {value}
                                </Badge>
                              )),
                            )}
                            {item.customizations.removed_ingredients.map(
                              (r) => (
                                <Badge
                                  key={r}
                                  variant="destructive"
                                  className="text-[10px] line-through"
                                >
                                  {r}
                                </Badge>
                              ),
                            )}
                          </div>
                          {item.customizations.special_instructions && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{item.customizations.special_instructions}"
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={isMutating}
                          className="text-muted-foreground hover:text-destructive transition-colors shrink-0 disabled:opacity-50"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 rounded-lg border border-border p-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isMutating}
                            onClick={() =>
                              item.quantity <= 1
                                ? removeItem(item.id)
                                : updateQuantity(item.id, item.quantity - 1)
                            }
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center text-sm font-bold text-foreground">
                            {item.quantity}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isMutating}
                            onClick={() =>
                              updateQuantity(item.id, item.quantity + 1)
                            }
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <span className="font-heading font-bold text-primary">
                          {formatPrice(item.total_price)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Clear Cart */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive gap-1"
                onClick={clearCart}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("cart.clearCart")}
              </Button>
            </div>

            {closedBranchMessage && (
              <Alert className="border-destructive/30 bg-destructive/5 text-foreground">
                <AlertTitle>{closedBranchMessage.title}</AlertTitle>
                <AlertDescription>
                  {closedBranchMessage.description}
                </AlertDescription>
              </Alert>
            )}

            {/* Branch Selection (when needed) */}
            {needsBranchSelection && (
              <div className="rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    <div>
                      <h3 className="font-heading font-semibold text-foreground">
                        {t("cart.selectBranch")}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {t("cart.selectBranchDesc")}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => openBranchModal()}>
                    {t("cart.chooseBranch")}
                  </Button>
                </div>
              </div>
            )}

            {/* Order Type */}
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-foreground">
                    {t("cart.orderType")}
                  </h3>
                  {orderType && (
                    <Badge variant="secondary" className="gap-1">
                      {orderTypeLabels[orderType]?.icon}
                      {orderTypeLabels[orderType]?.label}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setShowOrderTypeSelector(!showOrderTypeSelector)
                  }
                >
                  {t("cart.change")}
                </Button>
              </div>
              {showOrderTypeSelector &&
                (() => {
                  const activeTypes = [
                    brand.isDineInActive && "dine_in",
                    brand.isPickUpActive && "pickup",
                    brand.isDeliveryActive && "delivery",
                  ].filter(Boolean) as ("dine_in" | "pickup" | "delivery")[];
                  const cols =
                    activeTypes.length >= 3
                      ? "grid-cols-3"
                      : activeTypes.length === 2
                        ? "grid-cols-2"
                        : "grid-cols-1";
                  return (
                    <div className={`grid ${cols} gap-2 mt-3`}>
                      {activeTypes.map((type) => (
                        <button
                          key={type}
                          onClick={async () => {
                            const newType = type;

                            if (newType === "delivery") {
                              openBranchModal({
                                step: "delivery-map",
                                onClose: () => {},
                                onComplete: async (payload) => {
                                  await new Promise((r) => setTimeout(r, 50));
                                  setOrderType(
                                    newType,
                                    brand.productsAvailableInAllBranches,
                                  );
                                  await refreshCart({
                                    orderType: newType,
                                    lat: payload?.lat ?? null,
                                    lon: payload?.lon ?? null,
                                    branchId: payload?.branchId ?? null,
                                  });
                                  setShowOrderTypeSelector(false);
                                },
                              });
                            } else {
                              
                                openBranchModal({
                                  step: "pickup-branch",
                                  onClose: () => {
                                    /* do nothing — stay on cart */
                                  },
                                  onComplete: async (payload) => {
                                    await new Promise((r) => setTimeout(r, 50));
                                    setOrderType(
                                      newType,
                                      brand.productsAvailableInAllBranches,
                                    );
                                    await refreshCart({ orderType: newType });
                                    setShowOrderTypeSelector(false);
                                  },
                                });
                               
                            }

                            // else {
                            //   if (
                            //     !session.order.branchId ||
                            //     brand.productsAvailableInAllBranches
                            //   ) {
                            //     openBranchModal({
                            //       step: "pickup-branch",
                            //       onClose: () => { /* do nothing — stay on cart */ },
                            //       onComplete: async (payload) => {
                            //       await new Promise(r => setTimeout(r, 50));
                            //         setOrderType(newType,brand.productsAvailableInAllBranches);
                            //         await refreshCart({ orderType: newType });
                            //         setShowOrderTypeSelector(false);
                            //       },
                            //     });
                            //   }
                            //   else {
                            //     setOrderType(newType,brand.productsAvailableInAllBranches);
                            //     await refreshCart({ orderType: newType });
                            //     setShowOrderTypeSelector(false);
                            //   }
                            // }
                          }}
                          className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-center text-xs font-medium transition-all ${
                            orderType === type
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-foreground hover:border-primary/50"
                          }`}
                        >
                          {orderTypeLabels[type]?.icon}
                          {orderTypeLabels[type]?.label}
                        </button>
                      ))}
                    </div>
                  );
                })()}
            </div>

            {/* Delivery Address */}
            {orderType === "delivery" && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {t("cart.deliveryAddress")}
                </h3>
                <Input
                  placeholder="123 Main St, City..."
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                />
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Truck className="h-4 w-4" />
                  {t("cart.deliveryTime")}: ~25-35 min
                </div>
              </div>
            )}

            {/* Order Notes */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-primary" />
                {t("cart.notes")}
              </h3>
              <textarea
                className="w-full rounded-lg border border-border bg-background p-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                rows={3}
                placeholder={t("cart.notesPlaceholder")}
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
              />
            </div>

            {/* Delivery Timing */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {brand.hasSchedule ? (
                <>
                  {/* ASAP option */}
                  <button
                    onClick={() => {
                      setScheduledDate(undefined);
                      setScheduledTimeSlot("");
                    }}
                    className={`flex items-center justify-between w-full p-4 transition-colors ${
                      !isScheduled ? "bg-primary/5" : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                          !isScheduled
                            ? "border-primary bg-primary"
                            : "border-muted-foreground/30"
                        }`}
                      >
                        {!isScheduled && (
                          <svg
                            className="h-3 w-3 text-primary-foreground"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {t("cart.asap")}
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                  <div className="border-t border-border" />
                  {/* Schedule for later option */}
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    className={`flex items-center justify-between w-full p-4 transition-colors ${
                      isScheduled ? "bg-primary/5" : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                          isScheduled
                            ? "border-primary bg-primary"
                            : "border-muted-foreground/30"
                        }`}
                      >
                        {isScheduled && (
                          <svg
                            className="h-3 w-3 text-primary-foreground"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {isScheduled
                        ? `${t("cart.scheduledFor")} ${format(scheduledDate!, "PPP")}${scheduledTimeSlot ? ` - ${scheduledTimeSlot}` : ""}`
                        : t("cart.scheduleLater")}
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    {t("cart.asapAvailable")}
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>

            {/* Schedule Date Modal */}
            <Dialog
              open={showScheduleModal}
              onOpenChange={setShowScheduleModal}
            >
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {t("cart.selectDate")}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4">
                  <Calendar
                    mode="single"
                    selected={scheduledDate}
                    onSelect={setScheduledDate}
                    disabled={(date) => date < new Date()}
                    className={cn("p-3 pointer-events-auto")}
                  />
                  <div className="w-full">
                    <label className="text-sm font-medium text-foreground mb-1.5 block">
                      {t("cart.selectTime")}
                    </label>
                    <Select
                      value={scheduledTimeSlot}
                      onValueChange={setScheduledTimeSlot}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("cart.selectTime")} />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 13 }, (_, i) => {
                          const hour = 8 + i;
                          const time24 = `${hour.toString().padStart(2, "0")}:00`;
                          const ampm = hour < 12 ? "AM" : "PM";
                          const h12 = hour % 12 || 12;
                          return (
                            <SelectItem key={time24} value={time24}>
                              {h12}:00 {ampm}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!scheduledDate || !scheduledTimeSlot}
                    onClick={() => setShowScheduleModal(false)}
                  >
                    {t("cart.confirm")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Payment Method */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="font-heading font-semibold text-foreground mb-3">
                {t("cart.payment")}
              </h3>
              {brand.paymentMethod === "both" ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSelectedPayment("card")}
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm font-medium transition-all ${
                      selectedPayment === "card"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-foreground hover:border-primary/50"
                    }`}
                  >
                    <CreditCard className="h-4 w-4" />
                    {t("cart.payOnline")}
                  </button>
                  <button
                    onClick={() => setSelectedPayment("cash")}
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm font-medium transition-all ${
                      selectedPayment === "cash"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-foreground hover:border-primary/50"
                    }`}
                  >
                    <Banknote className="h-4 w-4" />
                    {t("cart.payCash")}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border-2 border-primary bg-primary/10 p-3 text-sm font-medium text-primary">
                  {brand.paymentMethod === "cash" ? (
                    <Banknote className="h-4 w-4" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  {brand.paymentMethod === "cash"
                    ? t("cart.payCash")
                    : t("cart.payOnline")}
                </div>
              )}
            </div>
          </div>

          {/* Right: Summary Sidebar */}
          <div className="space-y-4">
            {/* Promo Code */}
            {brand.allowCoupon && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  {t("cart.promoCode")}
                </h3>
                <div className="flex gap-2">
                  <Input
                    placeholder="BOGO, COMBO20..."
                    value={promoCode}
                    disabled={!isPromoEnabled}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoError("");
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => requireAuth(() => handleApplyPromo())}
                    disabled={promoLoading || !isPromoEnabled}
                  >
                    {promoLoading ? "..." : t("cart.apply")}
                  </Button>
                </div>
                {promoApplied && promoDiscount && (
                  <p className="text-sm text-success font-medium">
                    ✓ {t("cart.promoApplied")} (
                    {promoDiscount.type === "percentage"
                      ? `${promoDiscount.value}%`
                      : formatPrice(promoDiscount.value)}{" "}
                    — {formatPrice(promoDiscount.amount)})
                  </p>
                )}
                {promoError && (
                  <p className="text-sm text-destructive font-medium">
                    ✗ {promoError}
                  </p>
                )}
              </div>
            )}

            {/* Loyalty Points - TODO: Integrate with backend loyalty system */}
            {/* <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                <Star className="h-4 w-4 text-warning" />
                {t("cart.loyalty")}
              </h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("cart.loyaltyBalance")}</span>
                <span className="font-bold text-foreground">0 pts</span>
              </div>
            </div> */}

            {/* Order Summary - All values from backend */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("cart.subtotal")}
                </span>
                <span className="text-foreground">{formatPrice(subtotal)}</span>
              </div>
              {backendDiscount > 0 && (
                <div className="flex items-center justify-between text-sm text-success">
                  <span>{t("cart.discount")}</span>
                  <span>-{formatPrice(backendDiscount)}</span>
                </div>
              )}
              {promoApplied && promoDiscount && (
                <div className="flex items-center justify-between text-sm text-success">
                  <span className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    {promoCode.toUpperCase()}
                  </span>
                  <span>-{formatPrice(promoDiscount.amount)}</span>
                </div>
              )}
              {shouldShowDeliveryFee && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t("cart.deliveryFee")}
                  </span>

                  {isDeliveryFree ? (
                    <span className="font-medium text-success">
                      {t("cart.free")}
                    </span>
                  ) : (
                    <span className="text-foreground">
                      {formatPrice(deliveryFee)}
                    </span>
                  )}
                </div>
              )}
              {tax > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("cart.tax")}</span>
                  <span className="text-foreground">{formatPrice(tax)}</span>
                </div>
              )}
              <div className="border-t border-border pt-3 flex items-center justify-between">
                <span className="font-heading font-bold text-foreground text-lg">
                  {t("cart.total")}
                </span>
                <span className="font-heading font-bold text-primary text-xl">
                  {formatPrice(total)}
                </span>
              </div>
            </div>

            {/* Minimum Order Warning */}
            {isBelowMinimum && (
              <div className="rounded-xl bg-warning/10 border border-warning/30 p-4 text-sm">
                <p className="text-warning font-medium text-center">
                  {language === "ar"
                    ? `الحد الأدنى للطلب ${formatPrice(orderMinimum)}. أضف ${formatPrice(amountNeeded)} للمتابعة.`
                    : `Minimum order is ${formatPrice(orderMinimum)}. Add ${formatPrice(amountNeeded)} more to proceed.`}
                </p>
              </div>
            )}

            {/* Place Order Button */}
            <Button
              size="lg"
              className="w-full gap-2 py-6 text-base font-bold rounded-xl"
              disabled={
                isPlacingOrder ||
                isMutating ||
                needsBranchSelection ||
                !!closedBranchMessage ||
                isBelowMinimum
              }
              onClick={() => requireAuth(() => handlePlaceOrder())}
            >
              {isBelowMinimum
                ? language === "ar"
                  ? `الحد الأدنى ${formatPrice(orderMinimum)}`
                  : `Minimum ${formatPrice(orderMinimum)}`
                : needsBranchSelection
                  ? t("cart.selectBranchFirst")
                  : closedBranchMessage
                    ? closedBranchMessage.title
                    : isPlacingOrder
                      ? t("cart.placingOrder")
                      : t("cart.placeOrder")}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
